import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, BookOpen, HelpCircle, ArrowRight } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const EducationPage = () => {
  const insuranceBasics = [
    {
      title: "Term Life Insurance",
      content: "Term life provides coverage for a specific period (10, 20, or 30 years). It's typically the most affordable option and pays a death benefit only if you pass away during the term. Ideal for covering specific obligations like mortgages or children's education expenses."
    },
    {
      title: "Whole Life Insurance",
      content: "Whole life provides permanent coverage with guaranteed premiums and death benefits. It also accumulates cash value over time that you can borrow against. Popular for estate planning and wealth transfer due to its predictability."
    },
    {
      title: "Universal Life Insurance",
      content: "Universal life offers flexible premiums and death benefits with a cash value component. Variations include Indexed Universal Life (IUL) which ties cash value growth to market indices, and Variable Universal Life (VUL) which allows investment in sub-accounts."
    },
    {
      title: "Understanding Death Benefits",
      content: "The death benefit is the amount paid to your beneficiaries when you pass away. It's typically income tax-free to recipients. The appropriate amount depends on your family's needs, debts, income replacement goals, and estate planning objectives."
    },
    {
      title: "Cash Value Explained",
      content: "Permanent life insurance policies (whole life, universal life) build cash value over time. This is essentially a savings component that grows tax-deferred. You can access it through loans or withdrawals, though this may reduce your death benefit."
    }
  ];

  const premiumFinancingFAQs = [
    {
      question: "What is premium financing?",
      answer: "Premium financing allows you to borrow money from a third-party lender to pay your life insurance premiums. The policy's cash value and death benefit serve as collateral. This strategy is designed for high-net-worth individuals who want substantial coverage while preserving liquid capital."
    },
    {
      question: "Who is eligible for premium financing?",
      answer: "Typically, premium financing is available to individuals with substantial net worth (usually $5M+), strong cash flow, and good credit. Lenders also consider your overall financial picture, existing insurance, and estate planning goals."
    },
    {
      question: "What are the risks of premium financing?",
      answer: "Key risks include interest rate fluctuations (if rates rise, your costs increase), policy underperformance (if cash value doesn't grow as projected), and potential personal liability if the policy doesn't support the loan. It requires ongoing monitoring and may not be suitable for everyone."
    },
    {
      question: "How do I exit a premium financing arrangement?",
      answer: "Exit strategies include using policy cash value to repay the loan, personal payment of the remaining balance, policy surrender, or death benefit proceeds paying off the loan. Proper planning for exit is crucial before entering any arrangement."
    }
  ];

  const generalFAQs = [
    {
      question: "How much life insurance do I need?",
      answer: "A common guideline is 10-12x your annual income, but the right amount depends on your specific situation: debts, dependents, spouse's income, estate planning goals, and lifestyle expectations. Our specialists can help you calculate your optimal coverage."
    },
    {
      question: "What factors affect my premium?",
      answer: "Key factors include age, health status, family medical history, tobacco use, occupation, hobbies, policy type, and coverage amount. Getting quotes earlier generally means lower premiums since rates increase with age."
    },
    {
      question: "Do I need a medical exam?",
      answer: "Many policies require a paramedical exam (height, weight, blood pressure, blood and urine samples). Some carriers offer no-exam policies for smaller amounts, though premiums are typically higher. Your health information helps carriers assess risk and determine pricing."
    },
    {
      question: "Can I buy life insurance for my parents?",
      answer: "Yes, you can purchase life insurance on a parent if you have an insurable interest and they consent to the policy and medical underwriting. This is common for estate planning, funeral expenses, or protecting a family business."
    },
    {
      question: "What is the difference between mutual and stock insurance companies?",
      answer: "Mutual companies are owned by policyholders and may pay dividends. Stock companies are owned by shareholders. Both can be financially strong—ratings matter more than structure. Many of America's most respected insurers are mutual companies."
    },
    {
      question: "How does ShopCompareInsure make money?",
      answer: "We receive commissions from insurance carriers when policies are placed, similar to any insurance agent or broker. We do not charge fees to clients. Our multi-carrier approach means we're not incentivized to push any single company's products."
    }
  ];

  return (
    <div className="bg-[#020617] min-h-screen pt-20" data-testid="education-page">
      {/* Hero Section */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-3xl">
            <span className="text-[#C5A059] text-sm uppercase tracking-widest mb-4 block">
              Knowledge Center
            </span>
            <h1 className="font-serif text-4xl md:text-5xl text-white leading-tight mb-6">
              Education & FAQs
            </h1>
            <p className="text-slate-300 text-xl leading-relaxed">
              Understanding life insurance is the first step to making informed decisions for your family. Explore our educational resources below.
            </p>
          </div>
        </div>
      </section>

      {/* Life Insurance Basics */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center space-x-3 mb-12">
            <BookOpen className="w-6 h-6 text-[#C5A059]" />
            <h2 className="font-serif text-3xl text-white">Life Insurance Basics</h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {insuranceBasics.map((item, index) => (
              <div
                key={index}
                className="bg-slate-900/50 border border-slate-800 p-6 hover:border-[#C5A059]/30 transition-colors"
              >
                <h3 className="text-white font-semibold text-lg mb-3">{item.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed">{item.content}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Premium Financing FAQs */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center space-x-3 mb-12">
            <HelpCircle className="w-6 h-6 text-[#C5A059]" />
            <h2 className="font-serif text-3xl text-white">Premium Financing FAQs</h2>
          </div>

          <div className="max-w-3xl">
            <Accordion type="single" collapsible className="space-y-4">
              {premiumFinancingFAQs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`pf-${index}`}
                  className="bg-slate-900/50 border border-slate-800 rounded-lg px-6 data-[state=open]:border-[#C5A059]/30"
                >
                  <AccordionTrigger className="text-white text-left hover:text-[#C5A059] hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* General FAQs */}
      <section className="py-24 bg-[#0F172A]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center space-x-3 mb-12">
            <HelpCircle className="w-6 h-6 text-[#C5A059]" />
            <h2 className="font-serif text-3xl text-white">General FAQs</h2>
          </div>

          <div className="max-w-3xl">
            <Accordion type="single" collapsible className="space-y-4">
              {generalFAQs.map((faq, index) => (
                <AccordionItem
                  key={index}
                  value={`gen-${index}`}
                  className="bg-slate-950/50 border border-slate-800 rounded-lg px-6 data-[state=open]:border-[#C5A059]/30"
                >
                  <AccordionTrigger className="text-white text-left hover:text-[#C5A059] hover:no-underline py-4">
                    {faq.question}
                  </AccordionTrigger>
                  <AccordionContent className="text-slate-400 pb-4">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <h2 className="font-serif text-3xl text-white mb-6">
            Still Have Questions?
          </h2>
          <p className="text-slate-400 mb-8 max-w-2xl mx-auto">
            Our specialists are ready to answer your questions and help you find the right coverage.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center bg-[#C5A059] text-[#020617] hover:bg-[#D4AF37] font-semibold px-8 py-4 uppercase tracking-wider text-sm transition-all"
            >
              Speak with a Specialist
            </Link>
            <Link
              to="/compare"
              className="inline-flex items-center justify-center border border-slate-600 text-slate-300 hover:border-[#C5A059] hover:text-[#C5A059] px-8 py-4 uppercase tracking-wider text-sm transition-all"
            >
              Get a Quote
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default EducationPage;
